import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useUser } from '../contexts/UserContext';
import { Task, TaskStatus, TaskPriority, TaskCategory } from '../types/task';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FilterListIcon from '@mui/icons-material/FilterList';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import Loading from './Loading';
import Sidebar from './Sidebar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { TaskAnalytics } from './TaskAnalytics';
import { useTheme } from '../contexts/ThemeContext';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';

export function Tasks() {
  const { profile } = useUser();
  const { theme } = useTheme();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [currentTab, setCurrentTab] = useState<TaskStatus>('todo');
  const [selectedCategory, setSelectedCategory] = useState<TaskCategory | 'all'>('all');
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'todo' as TaskStatus,
    priority: 'medium' as TaskPriority,
    category: 'other' as TaskCategory,
    due_date: '',
  });

  const fetchTasks = useCallback(async () => {
    if (!profile) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .eq('user_id', profile.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching tasks:', error);
    } else {
      setTasks(data || []);
    }
    setLoading(false);
  }, [profile]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleOpenDialog = (task?: Task) => {
    if (task) {
      setEditingTask(task);
      setFormData({
        title: task.title,
        description: task.description || '',
        status: task.status,
        priority: task.priority,
        category: task.category,
        due_date: task.due_date || '',
      });
    } else {
      setEditingTask(null);
      setFormData({
        title: '',
        description: '',
        status: 'todo',
        priority: 'medium',
        category: 'other',
        due_date: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTask(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;

    const taskData = {
      ...formData,
      user_id: profile.id,
      updated_at: new Date().toISOString(),
    };

    if (editingTask) {
      const { error } = await supabase
        .from('tasks')
        .update(taskData)
        .eq('id', editingTask.id);

      if (error) {
        console.error('Error updating task:', error);
        return;
      }
    } else {
      const { error } = await supabase
        .from('tasks')
        .insert([{ ...taskData, created_at: new Date().toISOString() }]);

      if (error) {
        console.error('Error creating task:', error);
        return;
      }
    }

    handleCloseDialog();
    fetchTasks();
  };

  const handleDeleteClick = (taskId: string) => {
    setTaskToDelete(taskId);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!taskToDelete) return;

    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', taskToDelete);

    if (error) {
      console.error('Error deleting task:', error);
      return;
    }

    setOpenDeleteDialog(false);
    setTaskToDelete(null);
    fetchTasks();
  };

  const handleStatusChange = async (task: Task, newStatus: TaskStatus) => {
    const { error } = await supabase
      .from('tasks')
      .update({
        status: newStatus,
        completed_at: newStatus === 'completed' ? new Date().toISOString() : null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', task.id);

    if (error) {
      console.error('Error updating task status:', error);
      return;
    }

    fetchTasks();
  };

  const filteredTasks = tasks.filter(task => {
    const statusMatch = task.status === currentTab;
    const categoryMatch = selectedCategory === 'all' || task.category === selectedCategory;
    return statusMatch && categoryMatch;
  });

  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const getCategoryColor = (category: TaskCategory) => {
    switch (category) {
      case 'work':
        return 'primary';
      case 'personal':
        return 'secondary';
      case 'health':
        return 'success';
      case 'learning':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) return <Loading />;
  if (!profile) return null;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Sidebar user={profile} />
      <Box sx={{ 
        flexGrow: 1, 
        p: { xs: 2, md: 3 }, 
        display: 'flex', 
        flexDirection: 'column',
        width: { xs: '100%', md: 'auto' }
      }}>
        <Card sx={{ 
          mb: 3, 
          boxShadow: 6, 
          borderRadius: 3, 
          bgcolor: 'background.paper',
          overflow: 'hidden'
        }}>
          <CardHeader
            title={
              <Typography variant="h5" fontWeight={700} color="text.primary">
                Tasks
              </Typography>
            }
            subheader={
              <Typography variant="body2" color="text.secondary">
                Manage your tasks and track your progress
              </Typography>
            }
          />
          <CardContent>
            <Box 
              display="flex" 
              justifyContent="space-between" 
              alignItems="center" 
              mb={3}
              flexDirection={{ xs: 'column', sm: 'row' }}
              gap={{ xs: 2, sm: 0 }}
            >
              <Box display="flex" gap={2} width={{ xs: '100%', sm: 'auto' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<AnalyticsIcon />}
                  onClick={() => setShowAnalytics(!showAnalytics)}
                  sx={{ 
                    width: { xs: '100%', sm: 'auto' },
                    borderColor: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      bgcolor: 'action.hover',
                    },
                    fontWeight: 600,
                    fontSize: '1rem',
                    py: 1.2
                  }}
                >
                  {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleOpenDialog()}
                  sx={{ 
                    width: { xs: '100%', sm: 'auto' },
                    bgcolor: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                    fontWeight: 600,
                    fontSize: '1rem',
                    py: 1.2
                  }}
                >
                  New Task
                </Button>
              </Box>
            </Box>

            {showAnalytics && (
              <Box mb={4}>
                <TaskAnalytics tasks={tasks} />
              </Box>
            )}

            <Stack direction="row" spacing={1} sx={{ mb: 2, overflowX: 'auto', pb: 1 }}>
              <Chip
                label="All Categories"
                onClick={() => setSelectedCategory('all')}
                color={selectedCategory === 'all' ? 'primary' : 'default'}
                variant={selectedCategory === 'all' ? 'filled' : 'outlined'}
                sx={{ fontWeight: 600 }}
              />
              {['work', 'personal', 'health', 'learning', 'other'].map((category) => (
                <Chip
                  key={category}
                  label={category.charAt(0).toUpperCase() + category.slice(1)}
                  onClick={() => setSelectedCategory(category as TaskCategory)}
                  color={selectedCategory === category ? getCategoryColor(category as TaskCategory) : 'default'}
                  variant={selectedCategory === category ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600 }}
                />
              ))}
            </Stack>

            <Tabs
              value={currentTab}
              onChange={(_, newValue) => setCurrentTab(newValue)}
              sx={{ 
                mb: 3,
                '& .MuiTab-root': {
                  minWidth: { xs: 'auto', sm: 120 },
                  px: { xs: 1, sm: 2 },
                  color: 'text.secondary',
                  fontWeight: 600,
                  '&.Mui-selected': {
                    color: 'primary.main',
                  },
                },
                '& .MuiTabs-indicator': {
                  bgcolor: 'primary.main',
                },
              }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="To Do" value="todo" />
              <Tab label="In Progress" value="in_progress" />
              <Tab label="Completed" value="completed" />
              <Tab label="Archived" value="archived" />
            </Tabs>

            <Box display="flex" flexDirection="column" gap={2}>
              {filteredTasks.map((task) => (
                <Card key={task.id} sx={{ 
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  borderRadius: 2,
                  '&:hover': {
                    boxShadow: theme.shadows[4],
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out',
                  }
                }}>
                  <CardHeader
                    title={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h6" color="text.primary" fontWeight={600}>
                          {task.title}
                        </Typography>
                        <Chip
                          label={task.priority}
                          size="small"
                          color={getPriorityColor(task.priority)}
                          sx={{ ml: 1, fontWeight: 600 }}
                        />
                        <Chip
                          label={task.category}
                          size="small"
                          color={getCategoryColor(task.category)}
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                    }
                    subheader={
                      <Typography variant="body2" color="text.secondary">
                        Due: {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No due date'}
                      </Typography>
                    }
                    action={
                      <Box display="flex" gap={1}>
                        <IconButton 
                          onClick={() => handleOpenDialog(task)}
                          sx={{ 
                            color: 'text.secondary', 
                            '&:hover': { 
                              color: 'primary.main',
                              bgcolor: 'action.hover'
                            } 
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleDeleteClick(task.id)}
                          sx={{ 
                            color: 'text.secondary', 
                            '&:hover': { 
                              color: 'error.main',
                              bgcolor: 'action.hover'
                            } 
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                        {task.status !== 'completed' && (
                          <IconButton 
                            onClick={() => handleStatusChange(task, 'completed')}
                            sx={{ 
                              color: 'text.secondary', 
                              '&:hover': { 
                                color: 'success.main',
                                bgcolor: 'action.hover'
                              } 
                            }}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        )}
                      </Box>
                    }
                    sx={{
                      '& .MuiCardHeader-action': {
                        margin: { xs: 0, sm: 'auto' },
                        display: 'flex',
                        alignItems: 'center'
                      }
                    }}
                  />
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      {task.description}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
              {filteredTasks.length === 0 && (
                <Typography variant="body1" color="text.secondary" textAlign="center" py={4}>
                  No tasks in this category
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>

        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: 6,
            }
          }}
        >
          <form onSubmit={handleSubmit}>
            <DialogTitle>
              <Typography variant="h5" fontWeight={700}>
                {editingTask ? 'Edit Task' : 'New Task'}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2} mt={1}>
                <TextField
                  label="Title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={3}
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as TaskStatus })}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="todo">To Do</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="archived">Archived</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={formData.priority}
                    label="Priority"
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as TaskPriority })}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Due Date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button 
                onClick={handleCloseDialog}
                sx={{ 
                  fontWeight: 500,
                  color: 'text.secondary',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                sx={{ 
                  fontWeight: 600, 
                  fontSize: '1rem', 
                  py: 1.2,
                  px: 3
                }}
              >
                {editingTask ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        <Dialog 
          open={openDeleteDialog} 
          onClose={() => setOpenDeleteDialog(false)}
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: 6,
            }
          }}
        >
          <DialogTitle>
            <Typography variant="h5" fontWeight={700}>
              Delete Task
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography color="text.secondary">
              Are you sure you want to delete this task?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button 
              onClick={() => setOpenDeleteDialog(false)}
              sx={{ 
                fontWeight: 500,
                color: 'text.secondary',
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteConfirm} 
              color="error"
              variant="contained"
              sx={{ 
                fontWeight: 600, 
                fontSize: '1rem', 
                py: 1.2,
                px: 3
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
} 